import { useState, useEffect, useRef } from "react";
import { get, ref, push, set } from "firebase/database";
import { db } from "../../functions/api_firebase";
import BarraSuperior from "../components/barra_superior";


const MONITOREO_DEFAULT = "monitoreo@coordinarseguridad.com";
const CONTACTOS_DEFAULT = { monitoreo: MONITOREO_DEFAULT, emergencias: Array(5).fill(null) };

const TIPOS = [
  { tipo: "Coacción", icono: "bi-exclamation-octagon-fill", color: "#dc2626", fondo: "#fef2f2", borde: "#fca5a5" },
  { tipo: "Fuego",    icono: "bi-fire",                     color: "#ea580c", fondo: "#fff7ed", borde: "#fdba74" },
  { tipo: "Médica",   icono: "bi-heart-pulse-fill",         color: "#2563eb", fondo: "#eff6ff", borde: "#93c5fd" },
];

const ESTILOS_PULSE = { animation: "sos-pulse 2s ease-in-out infinite" };


const leerLS = (clave) => localStorage.getItem(clave) || "";

const fechaAhora = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};


const obtenerUbicacion = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: 0, lng: 0 });
    const fin = () => resolve({ lat: 0, lng: 0 });
    const t = setTimeout(fin, 5000);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => { clearTimeout(t); resolve({ lat: coords.latitude, lng: coords.longitude }); },
      () => { clearTimeout(t); fin(); },
      { timeout: 5000, maximumAge: 60000 }
    );
  });

const cargarContactos = async (nit, correoLimpio) => {
  try {
    const [snapCliente, snapCorreo] = await Promise.all([
      get(ref(db, `clientes/${nit}`)),
      get(ref(db, `correos/${correoLimpio}`)),
    ]);

    const [snapMonitoreo, ...snapsEmergencia] = await Promise.all([
      get(ref(db, `coordi_ph_clientes/${nit}/${snapCliente.val()}/correoMonitoreo`)),
      ...Array.from({ length: 5 }, (_, i) =>
        get(ref(db, `usuario/${correoLimpio}/${snapCorreo.val()}/correoEmergencia${i + 1}`))
      ),
    ]);

    return {
      monitoreo:   snapMonitoreo.val() || MONITOREO_DEFAULT,
      emergencias: snapsEmergencia.map((s) => s.val() || null),
    };
  } catch {
    return CONTACTOS_DEFAULT;
  }
};

const enviarAlerta = async (tipo, contactos, ubicacion) => {
  const nit            = leerLS("CodigoCliente");
  const razonSocial    = leerLS("RazonSocial");
  const nombreCompleto = leerLS("NombreCompleto");
  const direccion      = leerLS("Direccion");
  const correo         = leerLS("CorreoElectronico");
  const telefono       = leerLS("Celular");
  const ahora          = fechaAhora();

  if (!nit) throw new Error("La sesión no tiene código de cliente (NIT).");

  const registrarSOS = () =>
    push(ref(db, `/sos/${nit}`), {
      cliente: razonSocial, nit, direccion, nombreCompleto,
      fechayhoraCreacion: ahora,
      latitud: String(ubicacion.lat), longitud: String(ubicacion.lng),
      correo, telefono,
      estado: "Pendiente", tipo,
    });

  const [resultadoSOS, resultadoFCM] = await Promise.allSettled([
    registrarSOS(),
    set(ref(db, `coordi_ph_clientes/${nit}/fcm_sos/vivienda`),
      `${direccion.toUpperCase()} - ${razonSocial.toUpperCase()} - ${ahora} - ${tipo}`
    ),
  ]);

  if (resultadoFCM.status === "rejected") {
    console.warn("No se pudo disparar la notificación push del SOS:", resultadoFCM.reason);
  }

  if (resultadoSOS.status === "rejected") {
    console.warn("Falló el registro del SOS, reintentando:", resultadoSOS.reason);
    await registrarSOS();
  }

  try {
    // Mismo endpoint y mismos campos que la app (backend.dart → /sos/enviarcorreo).
    await fetch("https://coordiph.herokuapp.com/sos/enviarcorreo", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...Object.fromEntries(contactos.emergencias.map((c, i) => [`correo_emergencia_${i + 1}`, c])),
        correo_monitoreo: contactos.monitoreo,
        cliente_usuario: razonSocial.toUpperCase(),
        nombre_usuario: nombreCompleto.toUpperCase(),
        direccion_usuario: direccion.toUpperCase(),
        telefono_usuario: telefono,
        latitud_usuario: String(ubicacion.lat),
        longitud_usuario: String(ubicacion.lng),
        tipo_panico: tipo,
        codigoCliente: nit,
      }),
    });
  } catch {  }
};


const BannerAdvertencia = () => (
  <div style={{ width: "100%", padding: 20, background: "linear-gradient(135deg,#fffbeb,#fef3c7)", border: "1px solid #fcd34d", borderRadius: 16 }}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <span style={{ fontSize: 28 }}>⚠️</span>
      <div>
        <p style={{ margin: "0 0 4px", fontWeight: 700, color: "#92400e", fontSize: 15 }}>ADVERTENCIA</p>
        <p style={{ margin: 0, fontSize: 13, color: "#78350f", lineHeight: 1.6 }}>
          La atención por parte del personal de seguridad se realizará solo dentro de la copropiedad.
        </p>
      </div>
    </div>
  </div>
);

const BotonEmergencia = ({ tipo, icono, color, fondo, borde, onClick, deshabilitado }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
    <button
      onClick={() => onClick(tipo)}
      disabled={deshabilitado}
      style={{
        ...ESTILOS_PULSE,
        width: 120, height: 120, borderRadius: "50%", border: "none",
        background: color, color: "#fff",
        cursor: deshabilitado ? "not-allowed" : "pointer",
        boxShadow: `0 8px 24px ${color}66`,
        outline: "none", opacity: deshabilitado ? 0.6 : 1,
        transition: "opacity 0.2s",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <i className={icono} style={{ fontSize: 44 }} />
    </button>
    <span style={{ padding: "6px 18px", background: fondo, border: `1px solid ${borde}`, borderRadius: 20, fontSize: 14, fontWeight: 600, color }}>
      {tipo}
    </span>
  </div>
);

const MensajeResultado = ({ mensaje }) => mensaje ? (
  <div style={{
    width: "100%", marginTop: 20, padding: "16px 20px", borderRadius: 12,
    background: mensaje.exito ? "#f0fdf4" : "#fef2f2",
    border: `1px solid ${mensaje.exito ? "#86efac" : "#fca5a5"}`,
    color: mensaje.exito ? "#166534" : "#991b1b",
    fontWeight: 600, fontSize: 14, textAlign: "center",
  }}>
    {mensaje.exito ? "✅" : "❌"} {mensaje.texto}
  </div>
) : null;


const Panico = () => {
  const [enviando, setEnviando] = useState(false);
  const [mensaje,  setMensaje]  = useState(null);
  const contactosRef = useRef(CONTACTOS_DEFAULT);

  const nit          = leerLS("CodigoCliente");
  const correoLimpio = leerLS("CorreoElectronico").replace(/[@.]/g, "");

  useEffect(() => {
    cargarContactos(nit, correoLimpio).then((c) => { contactosRef.current = c; });
  }, [nit, correoLimpio]);

  const manejarToque = async (tipo) => {
    if (enviando) return;
    setEnviando(true);
    try {
      const ubicacion = await obtenerUbicacion();
      await enviarAlerta(tipo, contactosRef.current, ubicacion);
      setMensaje({ texto: "¡Pánico enviado correctamente!", exito: true });
    } catch {
      setMensaje({ texto: "Error al enviar pánico. Intenta de nuevo.", exito: false });
    } finally {
      setEnviando(false);
      setTimeout(() => setMensaje(null), 4000);
    }
  };

  return (
    <>
      <style>{`
        @keyframes sos-pulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.08); }
        }
      `}</style>

      <BarraSuperior />

      <div style={{ minHeight: "calc(100vh - 64px)", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 24px", maxWidth: 580, margin: "0 auto", gap: 0 }}>

        <BannerAdvertencia />

        <div style={{ textAlign: "center", marginTop: 28, marginBottom: 4 }}>
          <p style={{ margin: "0 0 6px", fontSize: 28, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" }}>Emergencia</p>
          <p style={{ margin: 0, fontSize: 14, color: "#6b7280", letterSpacing: "0.01em" }}>Selecciona el tipo de emergencia</p>
        </div>

        <div style={{ width: "100%", height: 1, background: "#f3f4f6", margin: "20px 0" }} />

        <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 32, width: "100%" }}>
          {TIPOS.map((t) => (
            <BotonEmergencia key={t.tipo} {...t} onClick={manejarToque} deshabilitado={enviando} />
          ))}
        </div>

        <div style={{ width: "100%", height: 1, background: "#f3f4f6", margin: "24px 0" }} />

        <div style={{ width: "100%", padding: 20, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ padding: 12, background: "#fef2f2", borderRadius: 12 }}>
            <i className="bi-hand-index-thumb-fill" style={{ fontSize: 22, color: "#dc2626" }} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 15, color: "#111827" }}>Toques restantes: 1</p>
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>Presiona 1 vez para enviar</p>
          </div>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>1</span>
          </div>
        </div>

        <MensajeResultado mensaje={mensaje} />

        {enviando && (
          <div style={{ marginTop: 20, color: "#6b7280", fontSize: 14 }}>Enviando alerta...</div>
        )}
      </div>
    </>
  );
};

export default Panico;
